import type { JSX, ReactNode } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useFocusTrap } from '../../hooks/useFocusTrap.js'
import {
  DrawerInfoButton,
  DrawerInfoPanel,
  useDrawerInfo,
  type DrawerInfoContent,
} from '../ui/DrawerInfo.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ResourceDrawerProps {
  /** Controls whether the drawer is visible. */
  open: boolean
  /** Called when the user closes the drawer (Cancel or backdrop click). */
  onClose: () => void
  /**
   * Called when the user clicks Save.
   * The drawer will show a loading state on the button until the promise settles.
   * If `formRef` is provided, this is ignored and the form's requestSubmit() is used instead.
   * If not provided, the Save button will not be rendered.
   */
  onSave?: () => Promise<void>
  /**
   * If provided, the Save button delegates to formRef.current?.requestSubmit() instead
   * of calling onSave directly. Enables form-driven drawers with react-hook-form.
   */
  formRef?: React.RefObject<HTMLFormElement | null>
  /** Title shown in the drawer header, e.g. "Create Skill". */
  title: string
  /**
   * Icon rendered inside the colored square in the header. Omit for the
   * default building glyph.
   */
  icon?: ReactNode
  /** Whether the save action is currently in progress. */
  saving?: boolean
  /**
   * Disables the Save button WITHOUT showing a loading spinner. Use this for
   * non-saving "cannot save right now" states (e.g. invalid form input). The
   * spinner remains driven by `saving` only. The button is disabled when
   * `saving || disabled`.
   */
  disabled?: boolean
  /**
   * 'standard' = max-w-md (default), 'wide' = max-w-2xl,
   * 'xl' = max-w-4xl — for tabbed / two-column forms that need the room,
   * '2xl' = max-w-6xl — for a step rail plus a two-column field grid beside it,
   * 'full' = max-w-[95vw] — for complex multi-step forms that need maximum space.
   */
  width?: 'standard' | 'wide' | 'xl' | '2xl' | 'full'
  /** Label for the Save button. Defaults to "Save". */
  saveLabel?: string
  /** What this drawer configures, behind an ⓘ button in the header. Omit and
   *  no button renders — see ui/DrawerInfo.tsx. */
  info?: DrawerInfoContent
  /**
   * Content pinned to the left end of the footer, opposite Cancel/Save — for a
   * multi-step drawer's "Step 2 of 5" counter and Back/Next buttons. Omit and
   * the footer keeps its default right-aligned actions.
   */
  footerStart?: ReactNode
  /** Form / content to render inside the drawer body. */
  children?: ReactNode
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Slide-in drawer (right-aligned overlay) for create/edit forms.
 *
 * Implemented with plain Tailwind (no external drawer library required) so
 * it works with the current dependency set (PrimeReact + Tailwind).
 *
 * Accessibility features:
 * - Backdrop closes the drawer on click.
 * - Escape key closes the drawer via a document-level keydown listener.
 * - Focus is trapped inside the panel while the drawer is open.
 * - Focus is restored to the triggering element when the drawer closes.
 */
export default function ResourceDrawer({
  open,
  onClose,
  onSave,
  formRef,
  title,
  icon,
  saving = false,
  disabled = false,
  width,
  saveLabel = 'Save',
  info,
  footerStart,
  children,
}: ResourceDrawerProps): JSX.Element {
  const trapRef = useFocusTrap<HTMLElement>(open)
  const drawerInfo = useDrawerInfo(open)
  const previousFocus = useRef<HTMLElement | null>(null)

  const internalSave = useCallback(async () => {
    if (formRef?.current) {
      formRef.current.requestSubmit()
    } else if (onSave) {
      await onSave()
    }
  }, [formRef, onSave])

  // Capture active element when opening; restore it when closing.
  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement as HTMLElement
    } else if (previousFocus.current) {
      previousFocus.current.focus()
      previousFocus.current = null
    }
  }, [open])

  // Move initial focus to the first focusable element inside the panel on open.
  useEffect(() => {
    if (!open) return
    const first = trapRef.current?.querySelector<HTMLElement>(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])',
    )
    first?.focus()
  }, [open, trapRef])

  // Close on Escape key while drawer is open (suppressed while saving).
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose, saving])

  if (!open) {
    return <></>
  }

  const widthClass =
    width === 'full'
      ? 'max-w-[95vw]'
      : width === '2xl'
        ? 'max-w-6xl'
        : width === 'xl'
          ? 'max-w-4xl'
          : width === 'wide'
            ? 'max-w-2xl'
            : 'max-w-md'

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <aside
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed right-0 top-[0px] z-[9999] flex h-[100vh] w-full ${widthClass} flex-col bg-white shadow-2xl`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center text-white">
              {icon ?? <i className="pi pi-building text-white text-sm" />}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{title}</h2>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {info && <DrawerInfoButton shown={drawerInfo.shown} onToggle={drawerInfo.toggle} />}
            <button
              type="button"
              className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
              aria-label="Close"
              onClick={onClose}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </div>
        </div>

        {info && (
          <DrawerInfoPanel info={info} shown={drawerInfo.shown} onDismiss={drawerInfo.toggle} />
        )}

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 bg-white">
          <div className="min-w-0">{footerStart}</div>
          <div className="flex shrink-0 items-center gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            {onSave && (
              <button
                onClick={() => { void internalSave() }}
                disabled={saving || disabled}
                className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
              >
                <i className={saving ? 'pi pi-spin pi-spinner text-xs' : 'pi pi-save text-xs'} aria-hidden="true" />
                {saving ? 'Saving…' : saveLabel}
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}
